import { useState } from 'react';
import type { FormEvent } from 'react';
import { QrCode } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { upiQrUrl } from '@/lib/upiQr';

const STORAGE_KEYS = { id: 'dawa.upi_id', name: 'dawa.upi_name' } as const;

function readStored(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

/**
 * A printable QR for accepting customer payments at the counter — separate
 * from the pharmacy's platform subscription payment (see Subscription page).
 * Stored locally per device rather than synced through the API: it's a
 * till-side convenience, not tenant data other staff or reports need.
 */
export function CounterUpiCard() {
  const { t } = useTranslation();
  const [upiId, setUpiId] = useState(() => readStored(STORAGE_KEYS.id));
  const [upiName, setUpiName] = useState(() => readStored(STORAGE_KEYS.name));
  const [saved, setSaved] = useState(() => Boolean(readStored(STORAGE_KEYS.id)));

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!upiId.trim() || !upiName.trim()) {
      toast({ title: 'UPI ID and payee name are both required', variant: 'destructive' });
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.id, upiId.trim());
      localStorage.setItem(STORAGE_KEYS.name, upiName.trim());
    } catch {
      /* private browsing — the QR still renders for this session */
    }

    setSaved(true);
    toast({ title: 'UPI details saved on this device', variant: 'success' });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <QrCode className="h-4 w-4" /> Counter UPI QR
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          For customers to scan and pay at this till. Saved on this device only.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="upi-id">{t('settingsUPIId')}</Label>
              <Input
                id="upi-id"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="e.g. 9876543210@upi"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="upi-name">Payee name</Label>
              <Input
                id="upi-name"
                value={upiName}
                onChange={(e) => setUpiName(e.target.value)}
                placeholder="e.g. Ansari Medicos"
              />
            </div>
            <Button type="submit" size="sm">
              {t('save')}
            </Button>
          </div>

          {saved && upiId && (
            <img
              src={upiQrUrl(upiId, upiName)}
              alt="UPI payment QR code"
              width={140}
              height={140}
              className="mx-auto shrink-0 rounded-lg border-2 border-border"
            />
          )}
        </form>
      </CardContent>
    </Card>
  );
}
